/* ============================================================
   Das Wörterbuch von Paket P7 (SPEC §13.7, §90.3): Bibliothek, Produkt,
   Admin-Prüfung (Bibliothek, Kategorien, Regeln, Ernte, Maschinen), KI-Assistent
   und Anreicherung. Hängt in `lib/texte/index.ts` als eigener Ast unter
   `t.bibliothek`; der Name des Exports ist Vertrag.

   Wortlaut nach dem Design-Canvas (Paket D, `.design/screens-bibliothek.mjs` und
   `screens-modell.mjs` für das KI-Fenster) — bei Wortlaut gewinnt der Canvas (§13).
   Wo der Canvas nichts sagt, gilt die Tonlage aus .impeccable.md: ruhig, präzise,
   Fachwörter bleiben Fachwörter. Fehlersätze des Servers kommen fertig aus `detail`
   und stehen deshalb NICHT hier.
   ============================================================ */
import type { DarstellungStatus, KartenArt, ProduktStatus, VwArt, VwRolle } from "../../api/types";

/** Zahl mit Einzahl oder Mehrzahl — im Deutschen genügen hier zwei Formen. */
const mz = (n: number, eins: string, viele: string) => `${n.toLocaleString("de-DE")} ${n === 1 ? eins : viele}`;

/** Eine Anzahl aus einer begrenzten Antwort (`GET /bibliothek` liefert höchstens 300
 *  Zeilen): ist sie voll, ist die Zahl nur eine Untergrenze — „300+". */
const anzahl = (n: number, voll = false) => `${n.toLocaleString("de-DE")}${voll ? "+" : ""}`;

export const bibliothek = {
  titel: "Bibliothek",
  anzahl,
  unterzeile: (bestaetigt: number, wartet: number, bestaetigtVoll = false, wartetVoll = false) =>
    [
      bestaetigtVoll ? `${anzahl(bestaetigt, true)} bestätigte Produkte` : mz(bestaetigt, "bestätigtes Produkt", "bestätigte Produkte"),
      wartet > 0 ? `${anzahl(wartet, wartetVoll)} ${wartet === 1 && !wartetVoll ? "wartet" : "warten"} auf Bestätigung` : null,
      "eingefügt wird eine Kopie, die Herkunft bleibt",
    ]
      .filter(Boolean)
      .join(" · "),
  suchePlatzhalter: "Produkt, Hersteller oder Kennung",
  sucheKurz: "Produkt oder Kennung",
  filter: {
    bestaetigt: "Bestätigt",
    wartet: "Wartet",
    alle: "Alle",
    archiviert: "Archiviert",
    abgelehnt: "Abgelehnt",
  },
  kategorien: "Kategorien",
  alleKategorien: "Alle",
  neuesProdukt: "Neues Produkt",
  neuKurz: "Neu",
  warteHinweis: (n: number, voll = false) => (n === 1 && !voll ? "1 Vorschlag wartet auf einen Admin." : `${anzahl(n, voll)} Vorschläge warten auf einen Admin.`),
  grenzeZaehler: (n: number) => `Die Zähler stammen aus den ersten ${n.toLocaleString("de-DE")} Treffern. Suche oder Kategorie grenzen ein.`,
  grenzeRaster: (n: number) => `Gezeigt werden die ersten ${n.toLocaleString("de-DE")} Produkte. Die Suche grenzt ein.`,
  pruefenLink: "Prüfen",
  leer: "Hier steht noch kein Produkt.",
  leerSuche: "Kein Produkt passt zu dieser Suche.",
  leerHinweis: "Produkte entstehen aus einer Karte („In Bibliothek vorschlagen“) oder hier über „Neues Produkt“.",
  ohneBild: "ohne Bild",
  fassungKurz: (v: number) => `v${v}`,

  status: {
    vorgeschlagen: "wartet auf Bestätigung",
    bestaetigt: "bestätigt",
    abgelehnt: "abgelehnt",
    archiviert: "archiviert",
  } satisfies Record<ProduktStatus, string>,

  art: { standard: "Standard", info: "Info", vegetation: "Vegetation", lawn: "Rasen" } satisfies Record<KartenArt, string>,

  /* --------------------------------------------------------- Neues Produkt */
  anlegen: {
    titel: "Neues Produkt",
    unterzeile: "Das Produkt wird vorgeschlagen. Bestätigt wird es von einem Admin, erst dann erscheint es im Einfügen-Dialog.",
    kategorie: "Kategorie",
    kategorieWaehlen: "Kategorie wählen",
    art: "Kartenart",
    label: "Label",
    labelPlatzhalter: "z. B. Sitzbank",
    anlegen: "Vorschlagen",
    angelegt: "Vorgeschlagen.",
  },

  /* ------------------------------------------------------------ Produktseite */
  produkt: {
    zurueck: "Zur Bibliothek",
    fassung: (v: number) => `Fassung ${v}`,
    geprueft: (wer: string, wann: string) => `geprüft von ${wer} am ${wann}`,
    vorgeschlagenVon: (wer: string, wann: string) => `vorgeschlagen von ${wer} am ${wann}`,
    // Ohne Person: das Konto ist gelöscht oder bewusst gelöst (Vorschläge, die ein
    // Prüfkonto angelegt hat, sollen nicht dessen Namen tragen).
    vorgeschlagenAm: (wann: string) => `vorgeschlagen am ${wann}`,
    kennungFolgt: "Kennung wird beim Bestätigen vergeben",
    abgelehnt: (grund: string) => (grund ? `Abgelehnt: ${grund}` : "Abgelehnt."),
    ersetzt:
      "Dieser Vorschlag ersetzt ein bestätigtes Produkt. Beim Bestätigen wird das alte archiviert; Kennung und Darstellungen in Vectorworks gehen auf diesen Vorschlag über.",
    ersetztOeffnen: "Bisheriges Produkt öffnen",
    alsFassung: "Als neue Fassung übernehmen",
    alsFassungHilfe: "„Als neue Fassung übernehmen“ macht den Inhalt stattdessen zur neuen Fassung des bisherigen Produkts; Karten sehen dann den Hinweis auf die neuere Fassung.",
    alsFassungOk: "Als neue Fassung des bisherigen Produkts übernommen.",
    reiterInhalt: "Inhalt",
    reiterBilder: "Bilder",
    bestaetigen: "Bestätigen",
    bestaetigt: "Bestätigt. Die Kennung ist vergeben.",
    ablehnen: "Ablehnen …",
    abgelehntOk: "Abgelehnt.",
    archivieren: "Archivieren",
    archiviertOk: "Archiviert. Bestehende Karten bleiben, wie sie sind.",
    archivierenFrage: "Archivieren? Das Produkt ist danach nicht mehr wählbar; Karten, die es schon nutzen, bleiben unberührt.",
    zurueckziehen: "Vorschlag zurückziehen",
    zurueckgezogen: "Vorschlag zurückgezogen.",
    zurueckziehenFrage: "Den Vorschlag zurückziehen? Er wird als abgelehnt („zurückgezogen“) abgelegt.",
    nurLesen: "Nur lesen — ändern dürfen Admins und, solange es ein Vorschlag ist, der Vorschlagende.",
    entwurfGesperrt: (status: string) =>
      `Nicht gespeichert — das Produkt ist inzwischen ${status} und lässt sich hier nicht mehr ändern. Die Felder zeigen noch die eigene Eingabe.`,
    entwurfVerwerfen: "Eingabe verwerfen",
    nichtGefunden: "Dieses Produkt gibt es nicht (mehr).",
    vw: {
      titel: "Kennung und Vectorworks",
      kennung: "Produktkennung",
      symbol: "VW-Symbol",
      datei: "Bibliotheksdatei",
      typ: "Symboltyp",
      typen: ["2D", "3D", "Hybrid (2D + 3D)"] as const,
      leer: "—",
      hilfe: "Die Kennung bleibt, auch wenn Titel oder Kategorie wechseln. Der Laptop schreibt sie als Katalog-ID in LL_Produkt.",
      /** §20.7: Symbol, Datei und Typ sind ab Wanderung 009 der Spiegel der Hauptdarstellung. */
      spiegel: "Wahrheit ist die Hauptdarstellung unten.",
    },
    verwendetIn: "Verwendet in",
    verwendetAnzahl: (n: number) => (n === 0 ? "noch keiner Karte" : mz(n, "Karte", "Karten")),
    fassungen: "Fassungen",
    keineFassungen: "Noch keine Fassung.",
  },

  /* ------------------------------------- Darstellung in Vectorworks (§20.7) */
  darstellung: {
    titel: "Darstellung in Vectorworks",
    hinzufuegen: "Darstellung hinzufügen",
    hinzufuegenKurz: "Hinzufügen",
    arten: {
      hybrid_symbol: "Hybridsymbol",
      symbol_3d: "3D-Symbol",
      symbol_2d: "2D-Symbol",
      pflanzenstil: "Pflanzenstil",
      belagstil: "Belagstil",
      landschaftsbereichstil: "Landschaftsbereichstil",
      bordsteinstil: "Bordsteinstil",
      wandstil: "Wandstil",
      zaunstil: "Zaunstil",
      gelaenderstil: "Geländerstil",
      treppe: "Treppe",
      material: "Material",
      mosaik: "Mosaik",
      schraffur: "Schraffur",
      textur: "Textur",
      bildfuellung: "Bildfüllung",
      datenstempelstil: "Datenstempelstil",
    } satisfies Record<VwArt, string>,
    rollen: {
      haupt: "Hauptdarstellung",
      detail: "Detail",
      komponente: "Komponente",
      fugenbild: "Fugenbild",
      textur: "Textur",
    } satisfies Record<VwRolle, string>,
    status: { vorgeschlagen: "vorgeschlagen", bestaetigt: "bestätigt", abgelehnt: "abgelehnt" } satisfies Record<DarstellungStatus, string>,
    keine: "keine VW-Darstellung",
    symbolTitel: "Symbol-Art (Ressource ist ein Symbol)",
    aenderungOffen: "Änderung offen",
    aenderungOffenTitel: "Die Maschine schlägt eine Änderung an dieser bestätigten Darstellung vor.",
    anzahl: (n: number) => mz(n, "Darstellung", "Darstellungen"),
    /** „üblich: Pflanzenstil → IfcGeographicElement (USERDEFINED · Spielgeräte)" — fehlt ein Teil, fällt er weg. */
    ueblich: (art: string, ifc: string, zusatz: string) =>
      `üblich: ${[art, ifc].filter(Boolean).join(" → ")}${zusatz ? ` (${zusatz})` : ""}`,
    leer: "Noch keine Darstellung in Vectorworks.",
    leerHinweis: "Der Laptop liefert sie beim Abgleich; ein Admin kann sie hier auch von Hand anlegen.",
    nichtGeladen: "Die Darstellungen ließen sich nicht laden.",
    draufsicht: "Draufsicht",
    iso: "Iso",
    ohneVorschau: (ansicht: string) => `${ansicht}: keine Vorschau`,
    vorschauGross: (ansicht: string, name: string) => `${ansicht} · ${name}`,
    perMaschine: (name: string) => `per ${name}`,
    wirdMitProdukt: "wird mit dem Produkt bestätigt",
    gesperrt: "Dieses Produkt ändert man nicht mehr — die Darstellungen stehen nur zur Auskunft da.",
    abgelehnt: (grund: string) => (grund ? `abgelehnt: ${grund}` : "abgelehnt"),
    bestaetigen: "Bestätigen",
    ablehnen: "Ablehnen …",
    bearbeiten: "Bearbeiten",
    loeschen: "Löschen",
    loeschenTitel: "Darstellung löschen",
    loeschenFrage: "Diese Darstellung löschen? Sie war nie bestätigt — gelöscht ist sie weg. Eine bestätigte lehnt man stattdessen ab.",
    vorschauLaden: (ansicht: string) => `${ansicht} hochladen`,
    vorschauHilfe: "PNG, JPEG oder WebP, bis 2 MB und 2048 px. Gespeichert wird ein PNG.",
    vorschauHochgeladen: "Vorschau hochgeladen.",
    bestaetigt: "Darstellung bestätigt.",
    abgelehntOk: "Darstellung abgelehnt.",
    geloescht: "Darstellung gelöscht.",
    uebernommen: "Änderung übernommen.",
    verworfen: "Änderung verworfen.",
    mehrAktionen: "Weitere Aktionen",
    aenderung: {
      titel: (wer: string | null, wann: string) => `Vorschlag ${wer ? `von ${wer}` : "der Maschine"}${wann ? ` · ${wann}` : ""}`,
      feld: "Feld",
      jetzt: "jetzt",
      vorgeschlagen: "vorgeschlagen",
      vorschau: (ansicht: string) => `Vorschau ${ansicht}`,
      uebernehmen: "Übernehmen",
      verwerfen: "Verwerfen",
      leer: "—",
      nurVorschau: "Nur eine neue Vorschau.",
    },
    /** Feldnamen in Gegenüberstellung und Dialog. */
    felder: {
      rolle: "Rolle",
      vw_art: "VW-Art",
      vw_ressource: "Ressourcenname",
      vw_ressource_frueher: "Frühere Namen",
      vw_bibliothek_datei: "Bibliotheksdatei",
      vw_ordnerindex: "Ordnerindex",
      vw_ressourcenordner: "Ressourcenordner",
      vw_ressourcentyp: "Ressourcentyp",
      vw_stil_subtyp: "Stil-Untertyp",
      vw_pio_name: "Plug-in-Objekt",
      vw_symboltyp: "Symboltyp",
      vw_symbolfarbe: "Symbolfarbe",
      vw_datei_stand: "Stand der Datei",
      vw_uuid: "VW-UUID",
      vw_tags: "Tags",
      ifc_entitaet: "IFC-Entität",
      ifc_predefined: "PredefinedType",
      ifc_object_type: "ObjectType",
      ifc_quelle: "IFC-Quelle",
      pset_abbildung: "Pset-Abbildung",
      datensatz: "Datensatz",
      material: "Material",
      lichtquelle_ies: "IES-Datei",
      herkunft_url: "Herkunft (Adresse)",
      herkunft_format: "Format der Vorlage",
      herkunft_abrufdatum: "Abgerufen am",
      herkunft_lizenzhinweis: "Lizenzhinweis",
    } as Record<string, string>,
    symboltypen: ["2D", "3D", "Hybrid"] as const,
    symbolfarben: { schwarz: "schwarz · Symbol", blau: "blau · Plug-in-Objekt", rot: "rot · Gruppe" } as Record<string, string>,
    ohneFarbe: "—",
    ifcQuellen: { vw: "aus VW", vorgabe: "Vorgabe der Kategorie", hand: "von Hand" } as Record<string, string>,
    dialog: {
      neu: "Darstellung hinzufügen",
      bearbeiten: "Darstellung bearbeiten",
      unterzeile: "Eine VW-Ressource, die dieses Produkt in Vectorworks zeigt: Symbol, Stil oder Material.",
      gruppeRessource: "Ressource",
      gruppeIfc: "IFC",
      gruppeHerkunft: "Herkunft der Vorlage",
      ausVw: "Aus der VW-Datei",
      ausVwLeer: "Noch nichts vom Laptop gelesen.",
      vorgabe: "Vorgabe der Kategorie",
      ressourcePlatzhalter: "LL <KAT> <Hersteller> <Produkt> <Maß>",
      dateiPlatzhalter: "LL_Ausstattung.vwx",
      dateiHilfe: "Relativ zum Arbeitsgruppenordner.",
      ordnerPlatzhalter: "z. B. Bänke",
      frueherHilfe: "Ein Name je Zeile, der jüngste zuerst. So findet der Laptop eine umbenannte Ressource wieder.",
      ifcHilfe: "Groß-/Kleinschreibung wie in IFC, z. B. IfcKerb.",
      psetHilfe: "Quelle ist ein Datensatzfeld (LL_Produkt.Hersteller) oder ein fester Wert.",
      psetName: "Pset",
      psetEigenschaft: "Eigenschaft",
      psetQuelle: "Quelle",
      psetFest: "fest",
      psetFestTitel: "Fester Wert statt Datensatzfeld",
      psetNeu: "Zeile hinzufügen",
      psetEntfernen: "Zeile entfernen",
      psetPlatzhalter: "LL_Produkt.Hersteller",
      vorschauEntfernen: (ansicht: string) => `${ansicht} entfernen`,
      konflikt: (v: number) => `Inzwischen geändert (Fassung ${v} der Darstellung). Die Felder zeigen noch die eigene Eingabe.`,
      konfliktLaden: "Aktuelle Zeile laden",
      konfliktMeine: "Eingabe darauf sichern",
      angelegt: "Darstellung angelegt.",
      gesichert: "Darstellung gesichert.",
      nichtsGeaendert: "Nichts geändert.",
    },
    /* Bibliothek prüfen (§20.7) */
    pruefen: {
      reiterProdukte: "Produkte",
      reiterDarstellungen: "Darstellungen",
      plus: (n: number) => `+${mz(n, "Darstellung", "Darstellungen")}`,
      plusTitel: "Offene Darstellungen dieses Vorschlags — sie werden mit dem Produkt bestätigt.",
      mitBestaetigt: (n: number) => `bestätigt auch bis zu ${mz(n, "Darstellung", "Darstellungen")}`,
      mitBestaetigtTitel: "Beim Ersetzen kann eine Darstellung des Vorschlags der gleichen Darstellung des bisherigen Produkts weichen — darum „bis zu“.",
      unterzeile: "Offene Darstellungen und Änderungsvorschläge der Maschine an bestätigten Produkten. Darstellungen eines Vorschlags kommen mit dem Produkt.",
      leer: "Keine Darstellung wartet.",
      waehlen: (name: string) => `${name} wählen`,
      gewaehlt: (n: number) => `${mz(n, "Darstellung", "Darstellungen")} ausgewählt`,
      laeuft: (n: number) => `${mz(n, "Darstellung wird", "Darstellungen werden")} bearbeitet …`,
      ergebnis: (aktion: "bestaetigen" | "ablehnen", ok: number) => `${ok.toLocaleString("de-DE")} ${aktion === "bestaetigen" ? "bestätigt" : "abgelehnt"}`,
      fehler: (n: number) => `${n.toLocaleString("de-DE")} mit Fehler — bleiben ausgewählt`,
      ohneAntwort: "Zu dieser Darstellung kam keine Antwort. Liste neu laden und noch einmal versuchen.",
      ablehnenTitel: (n: number) => (n === 1 ? "Darstellung ablehnen" : `${n.toLocaleString("de-DE")} Darstellungen ablehnen`),
      ablehnenUnterzeile: "Bei einer offenen Änderung wird nur die Änderung verworfen; die bestätigte Darstellung bleibt.",
      zumProdukt: "Zum Produkt",
    },
  },

  /* ------------------------------------------------------- Ablehnen-Dialog */
  ablehnenDialog: {
    titel: "Vorschlag ablehnen",
    unterzeile: "Der Grund steht danach beim Vorschlag, damit der Vorschlagende ihn liest.",
    grund: "Grund",
    grundPlatzhalter: "z. B. gibt es schon als AUS-BANK-0012",
    absenden: "Ablehnen",
  },

  /* ------------------------------------------------------------ Formular */
  formular: {
    kartenart: "Kartenart",
    kategorie: "Kategorie",
    label: "Label",
    labelHilfe: "Umschalt+Enter bricht die Zeile um. Großschreibung macht die Vorlage.",
    labelZeigen: "Label zeigen",
    titelZuerst: "Titel vor Hersteller",
    hersteller: "Hersteller",
    herstellerlink: "Herstellerlink",
    adresseFalsch: "Die Adresse beginnt mit https:// (oder http://). Bis dahin wird sie nicht gesichert.",
    adresseOffen: "Herstellerlink noch nicht gesichert",
    anreichern: "Anreichern …",
    produkttitel: "Produkttitel",
    botanisch: "Botanischer Name",
    deutsch: "Deutscher Name",
    felder: "Felder",
    feld: "Feld",
    gruppe: (h: string) => (h ? `Gruppe ${h}` : "Gruppe"),
    gruppeNeu: "Gruppe",
    gruppeTitel: "Überschrift der Gruppe",
    gruppeLoeschen: "Gruppe entfernen",
    schluessel: "Schlüssel",
    wert: "Wert",
    rot: "Rot",
    rotHinweis: "Wert rot drucken (Hervorhebung)",
    feldLoeschen: "Feld entfernen",
    nachOben: "Nach oben",
    nachUnten: "Nach unten",
    text: "Text",
    abschnitte: "Abschnitte (Info-Karte)",
    abschnitt: "Abschnitt",
    zwischentitel: "Zwischenüberschrift",
    absatz: "Absatz",
    abschnittLoeschen: "Abschnitt entfernen",
    mischungTitel: "Titel der Mischung",
    mischung: "Mischung (eine Zeile je Bestandteil)",
    speichert: "Speichert …",
    gespeichert: (v: number) => `Gespeichert · v${v}`,
    konflikt: "Nicht gespeichert — Konflikt",
    konfliktText: (wer: string | null, v: number) =>
      `Inzwischen geändert${wer ? ` von ${wer}` : ""} (Fassung ${v}). Was nur eine Seite geändert hat, ist zusammengeführt; diese Felder haben beide verschieden geändert:`,
    konfliktMeine: "Meine Änderungen übernehmen",
    konfliktVerwerfen: "Aktuellen Stand behalten",
    meins: "Meins",
    aktuell: "Aktuell",
    mehr: "mehr",
    weniger: "weniger",
    fremdText: (wer: string | null, v: number) =>
      `${wer ?? "Jemand"} hat dieses Produkt geändert (Fassung ${v}). Die eigene Eingabe ist noch nicht gesichert.`,
    neuLaden: "Neu laden",
    fremdSichern: "Eingabe darauf sichern",
    an: "an",
    aus: "aus",
    bilderAnzahl: (n: number) => mz(n, "Bild", "Bilder"),
    nebenprodukt: "Nebenprodukt",
    nebenTitel: "Titel",
    nebenBild: "Bild",
    keinBild: "Kein Bild",
    /** Namen der obersten Schlüssel für die Konfliktleiste (§4.8). */
    schluesselNamen: {
      label: "Label",
      show_label: "Label zeigen",
      title_first: "Titel vor Hersteller",
      manufacturer: "Hersteller",
      title: "Produkttitel",
      source_url: "Herstellerlink",
      botanical: "Botanischer Name",
      common: "Deutscher Name",
      fields: "Felder",
      groups: "Gruppen",
      text: "Text",
      description: "Abschnitte",
      mix_title: "Titel der Mischung",
      mix: "Mischung",
      images: "Bilder",
      extra_images: "Weitere Bilder",
      farbauswahl: "Farbauswahl",
      side_item: "Nebenprodukt",
      art: "Kartenart",
      kategorie_id: "Kategorie",
    } as Record<string, string>,
    fehler: "Nicht gespeichert",
  },

  /* ---------------------------------------------------------------- Bilder */
  bilder: {
    titel: "Bilder",
    ablegen: "Bilder hierher ziehen oder",
    waehlen: "Dateien wählen",
    formate: "JPEG, PNG, WebP, HEIC, TIFF, SVG oder PDF, je bis 40 MB.",
    laedtHoch: (n: number) => `${n} ${n === 1 ? "Bild wird" : "Bilder werden"} hochgeladen …`,
    hochgeladen: (n: number) => `${n} ${n === 1 ? "Bild" : "Bilder"} hochgeladen.`,
    leer: "Noch kein Bild.",
    wirdAufbereitet: "wird aufbereitet",
    rolle: "Rolle",
    rollen: {
      hero: "Heldenbild",
      swatch: "Farbmuster",
      detail: "Detail",
      sketch: "Skizze",
      side: "Nebenbild",
      palette: "Palette",
      plan: "Plan",
    } as Record<string, string>,
    aufKarte: "auf der Karte",
    nichtAufKarte: "nicht auf der Karte",
    aufnehmen: "Auf die Karte",
    herausnehmen: "Von der Karte nehmen",
    rechte: "Rechte …",
    loeschen: "Bild löschen",
    loeschenFrage: "Das Bild aus dem Produkt löschen?",
    geloescht: "Bild gelöscht.",
    einordnung: (rolle: string, fit: string) => `Vorschlag: ${rolle}${fit === "contain" ? ", eingepasst" : ""}`,
    vorschlagUebernehmen: "Übernehmen",
    vorschlagGilt: "gilt schon",
    fit: "Einpassen",
    fitArten: { cover: "füllen", contain: "einpassen" } as Record<"cover" | "contain", string>,
    fokus: "Fokus",
    fokusHilfe: "Fokuspunkt 0–1, waagrecht und senkrecht; leer = Bildmitte.",
    fokusX: "Fokus waagrecht (0–1)",
    fokusY: "Fokus senkrecht (0–1)",
    hinweis: "Bildhinweis",
    hinweisPlatzhalter: "Bildhinweis, z. B. Nur Beispielbild, geänderte Anordnung",
    nachOben: "Nach vorn",
    nachUnten: "Nach hinten",
    farbauswahl: "Farbauswahl",
    farbauswahlVorgabe: "Farbauswahl",
    farbauswahlLabel: "Beschriftung",
    farbauswahlBild: "Bild",
    keinBild: "Kein Bild",
  },

  rechteDialog: {
    titel: "Bildrechte",
    unterzeile: "Ohne geklärte Rechte steht im Abgabe-PDF ein Platzhalter.",
    lizenz: "Lizenz",
    lizenzen: { eigen: "Eigenes Bild", erlaubt: "Nutzung erlaubt", angefragt: "Erlaubnis angefragt", unbekannt: "Unbekannt" } as Record<string, string>,
    warnung: "Im Abgabe-PDF erscheint ein Platzhalter.",
    credit: "Bildnachweis (Credit)",
    quelle: "Quelle (Adresse)",
    notiz: "Notiz",
    gesichert: "Rechte gesichert.",
  },

  /* ----------------------------------------------------- Produktsuche (§90.4) */
  suche: {
    platzhalter: "Produkt, Hersteller oder Kennung",
    leer: "Kein bestätigtes Produkt passt.",
    nurKategorie: (name: string) => `nur ${name}`,
    treffer: "Treffer",
  },

  /* ---------------------------------------------------- Admin: Bibliothek prüfen */
  pruefen: {
    titel: "Bibliothek prüfen",
    warten: (n: number, voll = false) => (n === 1 && !voll ? "1 wartet" : `${anzahl(n, voll)} warten`),
    grenze: (n: number) =>
      `Es warten mindestens ${n.toLocaleString("de-DE")} Vorschläge; die Liste zeigt die neuesten. Ältere rücken nach, sobald diese entschieden sind.`,
    unterzeile: "Vorschläge aus dem Büro. Erst nach dem Bestätigen erscheinen sie im Einfügen-Dialog.",
    oeffnen: "Öffnen",
    ausKarte: "aus einer Karte",
    neu: "neu angelegt",
    hinweis:
      "Beim Bestätigen vergibt der Katalog die Produktkennung. Inhalt, Kategorie und Art lassen sich vorher noch ändern — jede Änderung ist eine neue Fassung.",
    leer: "Kein Vorschlag wartet.",
    kategorieGeaendert: "Kategorie geändert.",
    mehrZeigen: (n: number) => `Weitere ${n} zeigen`,
    sichtbar: (n: number, gesamt: number) => `${n} von ${gesamt}`,
  },

  /* ----------------------------------------------------------- Admin: Kategorien */
  kategorienAdmin: {
    titel: "Kategorien",
    unterzeile: "Stammdaten für Bibliothek, Kapitel und Zuordnung: Schlüssel, VW-Kürzel, Namensmerkmale, Kennungskürzel.",
    neu: "Neue Kategorie",
    bearbeiten: "Kategorie bearbeiten",
    name: "Name",
    schluessel: "Schlüssel",
    schluesselHilfe: "Buchstaben, Ziffern und Bindestrich, 2–31 Zeichen, vorn ein Buchstabe, z. B. VEG-BAUM (wird groß geschrieben). Wird in Regeln und Vorschlägen benutzt.",
    eltern: "Elternkategorie",
    keineEltern: "keine (Hauptkategorie)",
    vwKuerzel: "VW-Kürzel",
    vwKuerzelHilfe: "Mit Komma getrennt, z. B. VEG, AUS. Ordnet VW-Klassen LL-<Kürzel>-… zu.",
    merkmale: "Namensmerkmale",
    merkmaleHilfe: "Mit Komma getrennt, z. B. baum, solitär. Unterscheidet Unterkategorien.",
    kapitelTitel: "Kapiteltitel",
    kartenArt: "Kartenart",
    sortierung: "Sortierung",
    aktiv: "Aktiv",
    inaktiv: "inaktiv",
    kennungKuerzel: "Kennungskürzel",
    kennungKuerzelHilfe:
      "2–4 Großbuchstaben (z. B. AUS). Leer: das Kürzel der Elternkategorie gilt. Ohne Kürzel lässt sich kein Produkt bestätigen.",
    geerbt: (kz: string) => `erbt ${kz}`,
    ohneKuerzel: "ohne Kürzel",
    gesichert: "Kategorie gesichert.",
    angelegt: "Kategorie angelegt.",
    leer: "Noch keine Kategorie.",
    /* Vorgabe für die Darstellung in Vectorworks (§20.7) */
    vwVorgabe: "VW-Vorgabe",
    vwVorgabeHilfe: "Nur Hinweis und Vorbelegung beim Anlegen einer Darstellung, nie Zwang. Leer: die der Elternkategorie gilt.",
    vwArt: "VW-Art",
    ifcEntitaet: "IFC-Entität",
    ifcPredefined: "PredefinedType",
    ifcObjectType: "ObjectType",
    keineVorgabe: "—",
    vonEltern: (name: string) => `(von ${name})`,
  },

  /* -------------------------------------------------------------- Admin: Regeln */
  regeln: {
    titel: "Regeln",
    zaehler: (vor: number, aktiv: number) => `${vor} ${vor === 1 ? "Vorschlag" : "Vorschläge"} · ${aktiv} aktiv`,
    intro:
      "Eine bestätigte Zuordnung wird im Projekt sofort zur Regel. Fürs ganze Büro gilt sie erst, wenn ein Admin sie hier bestätigt — ein Fehler hier landet in jedem neuen Projekt.",
    reiter: { vorgeschlagen: "Vorschläge", aktiv: "Aktiv", aus: "Abgeschaltet", abgelehnt: "Abgelehnt" } as Record<string, string>,
    geltung: { buero: "Büroregeln", projekt: "Projektregeln" } as Record<string, string>,
    geltungEins: { buero: "Büroregel", projekt: "Projektregel" } as Record<string, string>,
    statusEins: { vorgeschlagen: "Vorschlag", aktiv: "aktiv", abgelehnt: "abgelehnt", aus: "abgeschaltet" } as Record<string, string>,
    treffer: (projekte: number, elemente: number) => `passt in ${mz(projekte, "Projekt", "Projekten")} auf ${mz(elemente, "Element", "Elemente")}`,
    angewandt: (n: number) => `${n.toLocaleString("de-DE")}× angewandt`,
    beispiele: "Beispiele",
    bestaetigen: "Bestätigen",
    lockern: "Muster lockern …",
    ablehnen: "Ablehnen",
    abschalten: "Abschalten",
    einschalten: "Wieder bestätigen",
    bestaetigt: "Regel bestätigt — sie gilt jetzt im ganzen Büro.",
    abgelehnt: "Regel abgelehnt.",
    abgeschaltet: "Regel abgeschaltet.",
    gesichert: "Regel gesichert.",
    leer: "Hier steht keine Regel.",
    ohneProdukt: "ohne Produkt",
    unbekannteKategorie: "unbekannte Kategorie",
    /** Eine Regel mit Wirkung „ignorieren" (§19.3 C8) ordnet nichts zu — statt der Kategorie. */
    ignorieren: "ignorieren",
    dialog: {
      titel: "Regel ändern",
      unterzeile: "Ein lockeres Muster trifft mehr Elemente — zum Beispiel die VW-Klasse statt der genauen Typsignatur.",
      art: "Muster",
      arten: { typsignatur: "Typsignatur", vw_klasse: "VW-Klasse", pset_wert: "Pset-Wert", name_muster: "Name" } as Record<string, string>,
      typHash: "Typ-Hash",
      klasse: "VW-Klasse",
      pset: "Pset",
      schluessel: "Eigenschaft",
      wert: "Wert",
      ifcKlasse: "IFC-Klasse (freiwillig)",
      nameIlike: "Name (ILIKE, % als Platzhalter)",
      kategorie: "Kategorie",
      wirkung: "Wirkung",
      wirkungen: { zuordnen: "Kategorie zuordnen", ignorieren: "Ignorieren" } as Record<string, string>,
      ignorierenHilfe: "Die Regel schlägt „ignorieren“ vor: ohne Kategorie und ohne Produkt, bestätigt wird in der Zuordnen-Liste.",
      produkt: "Produkt (freiwillig)",
      produktWaehlen: "Produkt wählen …",
      produktEntfernen: "Ohne Produkt",
    },
  },

  /* --------------------------------------------------------------- Admin: Ernte */
  ernte: {
    titel: "Ernte aus Vectorworks",
    unterzeile:
      "Hybridsymbole und Stile, die der Laptop aus den VW-Dateien geerntet hat. Behalten legt einen Vorschlag mit seiner Darstellung an, Zusammenführen setzt die Darstellung an einem vorhandenen Produkt.",
    lauf: "Lauf",
    alleLaeufe: "Alle Läufe",
    laufText: (wann: string, n: number) => `Lauf ${wann} · ${mz(n, "Symbol", "Symbole")}`,
    zustand: { offen: "Offen", behalten: "Behalten", zusammengefuehrt: "Zusammengeführt", verworfen: "Verworfen" } as Record<string, string>,
    zustandEins: { offen: "offen", behalten: "behalten", zusammengefuehrt: "zusammengeführt", verworfen: "verworfen" } as Record<string, string>,
    spalten: {
      vorschau: "Vorschau",
      projekt: "Projekt",
      datei: "Datei",
      symbol: "Symbol",
      art: "Art",
      hersteller: "IFC-Hersteller",
      zustand: "Zustand",
    },
    arten: ["2D", "3D", "Hybrid"] as const,
    ohneVorschau: "keine Vorschau",
    behalten: "Behalten",
    zusammenfuehren: "Zusammenführen …",
    verwerfen: "Verwerfen",
    waehlen: (name: string) => `${name} wählen`,
    alleWaehlen: "Alle wählen",
    auswahl: (n: number) => mz(n, "Symbol", "Symbole"),
    alleBehalten: "Alle behalten",
    auswahlAufheben: "Auswahl aufheben",
    behaltenTitel: "Als Produkt behalten",
    behaltenTitelViele: (n: number) => `${n} Symbole als Produkte behalten`,
    behaltenHilfe: "Legt ein Produkt mit Status „vorgeschlagen“ an. Hersteller, Modell und Artikelnummer kommen aus Pset_ManufacturerTypeInformation.",
    zusammenTitel: "Zusammenführen mit",
    zusammenHilfe: "Setzt die Darstellung (Symbol oder Stil, Datei, Vorschauen) am gewählten Produkt. Die Fassung des Produkts bleibt.",
    zusammenAbsenden: "Zusammenführen",
    entschieden: (n: number) => (n === 1 ? "Entschieden." : `${n} Symbole entschieden.`),
    teilweise: (ok: number, fehler: number) => `${ok} entschieden, ${fehler} mit Fehler.`,
    leer: "Keine Symbole in diesem Zustand.",
    leerHinweis: "Der Laptop liefert neue Läufe über POST /api/maschine/ernte (Token unter „Maschinen“).",
    zumProdukt: "Zum Produkt",
    vorschau3d: (name: string) => `${name} · Iso`,
  },

  /* ----------------------------------------------------------- Admin: Maschinen */
  maschinen: {
    titel: "Maschinen-Tokens",
    unterzeile: "Für den Laptop mit der VW-Bürobibliothek. Ein Token gilt nur für /api/maschine/* — 600 Anfragen je 15 Minuten, Uploads bis 20 MB.",
    neu: "Neues Token",
    spalten: { name: "Name", rechte: "Rechte", angelegt: "Angelegt", zuletzt: "Zuletzt benutzt", zustand: "Zustand" },
    aktiv: "aktiv",
    widerrufen: "widerrufen",
    widerrufenKnopf: "Widerrufen",
    widerrufenFrage: (name: string) => `Das Token „${name}“ widerrufen? Der Laptop kommt danach nicht mehr herein, bis er ein neues bekommt.`,
    widerrufenOk: "Token widerrufen.",
    nie: "noch nie",
    leer: "Noch kein Token.",
    anlegenTitel: "Neues Maschinen-Token",
    anlegenUnterzeile: "Das Token wird gleich danach einmal im Klartext gezeigt.",
    name: "Name",
    namePlatzhalter: "worklaptop",
    rechte: "Rechte",
    rechtLesen: "Produkte lesen",
    rechtErnte: "Ernte schreiben",
    anlegen: "Anlegen",
    tokenTitel: (name: string) => `Token für „${name}“`,
    tokenWarnung: "Das Token steht nur jetzt im Klartext da. Gespeichert wird nur sein Hash — wer es verliert, legt ein neues an.",
    kopieren: "Kopieren",
    kopiert: "Kopiert",
    tokenHilfe: "Auf dem Laptop nur in der Benutzer-Umgebung ablegen, nie in ein Repo. Kopfzeile: Authorization: Bearer kt_m_…",
    fertig: "Fertig",
  },

  /* ------------------------------------------------------------ KI-Assistent */
  ki: {
    titel: "KI-Assistent",
    starter: "KI-Assistent öffnen",
    schliessen: "Schließen",
    neu: "Neues Gespräch",
    verlauf: "Frühere Gespräche",
    gespraechsverlauf: "Gesprächsverlauf",
    verlaufLeer: "Noch kein früheres Gespräch.",
    verlaufLoeschen: "Gespräch löschen",
    bezug: {
      modell: (n: number) => (n > 0 ? `Bezug: Modell · ${n.toLocaleString("de-DE")} ausgewählt` : "Bezug: Modell"),
      karte: "Bezug: Karte",
      katalog: "Bezug: Katalog",
      projekt: "Bezug: Projekt",
    },
    leerTitel: "Was willst du wissen?",
    leerText: "Der Assistent liest Modell und Katalog. Geändert wird nur über Vorschläge, die du übernimmst.",
    startfragen: [
      "Wie viele Bäume sind geplant, nach Art?",
      "Welche Flächen haben noch keine Zuordnung?",
      "Wie viel m² Pflaster liegen im Modell?",
      "Welche Karten haben ungeklärte Bildrechte?",
    ],
    eingabe: "Frage zum Modell oder Katalog …",
    senden: "Senden",
    stopp: "Antwort abbrechen",
    rueckfall: "Ausweichweg, dauert länger.",
    wechsel: (label: string) => `Weiter mit ${label}`,
    belegAuswaehlen: (n: number) => `${mz(n, "Element", "Elemente")} auswählen`,
    abfrage: "Abfrage ansehen",
    abfrageZu: "Abfrage ausblenden",
    gekuerzt: "gekürzt",
    fehlerNetz: "Die Antwort ließ sich gerade nicht erzeugen. In einer Minute noch einmal versuchen.",
    abgebrochen: "Abgebrochen.",
    denkt: "Antwort entsteht …",
    status: { bereit: "bereit", ausweich: "nur Ausweichweg", weg: "nicht erreichbar", aus: "nicht eingerichtet" },
    werkzeug: {
      schema: "Lese das Schema",
      pset_schluessel: "Lese die Pset-Schlüssel",
      werte: "Lese die Werte",
      zaehlen: "Zähle",
      filtern: "Suche Elemente",
      element: "Lese Element",
      typgruppen: "Lese die Typgruppen",
      zuordnungen: "Lese die Zuordnungen",
      karten: "Lese die Karten",
      bibliothek_suchen: "Suche in der Bibliothek",
      sql: "Frage die Datenbank",
      vorschlagen: "Bereite Vorschlag vor",
    } as Record<string, string>,
    vorschlag: {
      titel: "Vorschlag",
      geprueft: "geprüft",
      hinweise: (n: number) => (n === 1 ? "1 Hinweis" : `${n} Hinweise`),
      uebernehmen: "Übernehmen",
      verwerfen: "Verwerfen",
      imModell: "Im Modell zeigen",
      angewandt: "Übernommen",
      verworfen: "Verworfen",
      abgelaufen: "Abgelaufen — bitte neu fragen.",
      // Alle Schlüssel, die der Server in `pruefung.zaehler` schickt (ki/vorschlaege.py) —
      // ein fehlender stand roh in der Karte („16 elemente“, Prüfung Runde 1).
      zaehler: {
        gefunden: "gefunden",
        fehlend: "fehlend",
        andere: "anders zugeordnet",
        anders_bestaetigt: "anders zugeordnet",
        elemente: "Elemente",
        karten: "Karten",
        ziele: "Ziele",
        schon_da: "schon da",
        felder: "Felder",
      } as Record<string, string>,
    },
  },

  /* ------------------------------------------------------------ Anreicherung */
  anreicherung: {
    titel: "Anreichern",
    kopf: (label: string) => (label ? `${label} · Anreichern` : "Anreichern"),
    unterzeile: "Felder und Bilder von der Herstellerseite als Vorschlag. Übernommen wird nur, was angehakt ist.",
    adresse: "Adresse der Herstellerseite",
    adressePlatzhalter: "https://…",
    starten: "Seite lesen",
    neueAdresse: "Andere Adresse",
    abgerufen: (wann: string) => `abgerufen ${wann}`,
    wartet: "Wartet auf den Browser …",
    laeuft: "Seite wird gelesen …",
    laeuftHinweis:
      "Die Seite wird im Browser des Servers geöffnet, danach werden Felder und Bilder ausgelesen. Das dauert meist unter einer halben Minute.",
    fehler: "Die Seite ließ sich nicht auslesen.",
    nochmal: "Noch einmal",
    seiteAnsehen: "Seite ansehen",
    original: "Original",
    spalten: { feld: "Feld", aktuell: "aktuell", vorgeschlagen: "vorgeschlagen", quelle: "Quelle" },
    nichtImText: "nicht im Seitentext gefunden",
    keineFelder: "Auf der Seite stand nichts, was sich als Feld übernehmen ließe.",
    bilderTitel: "Bilder von der Seite",
    bilderZahl: (n: number) => `${n} gefunden · Lizenz unbekannt`,
    keineBilder: "Keine Bilder gefunden.",
    rolle: "Rolle",
    bildWaehlen: "Bild übernehmen",
    feldWaehlen: (pfad: string) => `${pfad} übernehmen`,
    passtNicht: "passt wohl nicht",
    quellen: { jsonld: "JSON-LD", microdata: "Microdata", opengraph: "OpenGraph", rdfa: "RDFa", llm: "KI" } as Record<string, string>,
    abgewiesen: (n: number) => `${n} Anfragen abgewiesen`,
    verwerfen: "Verwerfen",
    uebernehmen: (felder: number, bilder: number) => {
      if (felder === 0 && bilder === 0) return "Nichts gewählt";
      const teile = [felder ? mz(felder, "Feld", "Felder") : null, bilder ? mz(bilder, "Bild", "Bilder") : null].filter(Boolean);
      return `${teile.join(" und ")} übernehmen`;
    },
    uebernommen: "Übernommen. Bilder kommen mit Lizenz „unbekannt“ — Rechte prüfen.",
    konflikt: "Inzwischen geändert. Bitte neu laden und noch einmal übernehmen.",
  },
} as const;

export type BibliothekTexte = typeof bibliothek;
